import {
  AdjustableConfigurationValue,
  AvailableOption,
  AvailablePreTestConfigurationOptions,
  AvailableVFASpecificConfigurationOptions,
  EyeType,
  TestCategory,
  TestConfiguration,
  TestLanguage,
  TestSpecificConfig,
  TestType,
  VFABehaviour,
  WaitDuration,
} from "types/testTypes";
import {
  CATEGORY_VERSION_MAP,
  FEATURE_VERSION_MAP,
} from "@constants/testConstants/versionConstants";
import {
  FIXATION_HANDLING_UPDATE,
  FOVEA,
  MIN_VIRTUAL_ASSISTANT_VERSION,
  NEW_LANGUAGES,
  NEW_LANGUAGES_2,
  NEW_LANGUAGES_3,
  NEW_PATTERNS,
  SCENE_UPDATE_MESSAGES,
  USB_UPDATE_NEEDED,
  VOICE_ASSISTANT,
} from "./api-communication/ApiConstants";
import { getOffersForDevice, getOffersForOrg } from "./api-communication/HeadsetReleasesCli";
import { isOfferedStatus, type ReleaseOffer } from "types/headsetReleaseTypes";

import { HeadsetVersion } from "types/productTypes";
import axios from "axios";
import { getHardwareModelFromPid } from "./productUtils";
import JsonData from "../data/data.json";
import { TEST_DEFINITIONS } from "@constants/testConstants/testDefinitions";
import { compareVersions, validate } from "compare-versions";
import { getChoiceValueListFromOption, isFreeFormNumberOption } from "./configurationUtils";
import { isBinocularVFATestType } from "@constants/testConstants/testSpecificConstants";
import { SCOPE_TYPE } from "@model";
import { hasVfaAdaptiveTracking } from "./VFA/adaptiveTrackingScope";
import { hasVfaPattern24_2C } from "./VFA/pattern24_2CScope";
import { supportsAssistantAudio } from "@constants/virtualAssistanceConstants";
import { store } from "@store/index";

/* Gets Version Info From data.json */
export function getLatestVersion() {
  // Ensure JsonData structure matches your actual JSON
  const latestVersion = JsonData.Versions.vfaVersions.versions[0].version;
  localStorage.setItem("latestVersion", latestVersion);
  return latestVersion;
}

/**
 * compareVersions throws on anything that is not valid semver, and devices in
 * the field do report non-semver builds (e.g. "2.8.5_dev"). Every version gate
 * below must therefore validate before comparing, or a single dev headset takes
 * down whatever renders the gate.
 */
function isValidVersion(version: HeadsetVersion | string | null | undefined): version is string {
  return typeof version === "string" && validate(version);
}

export function isAtLeastVersion(
  productVersion: HeadsetVersion | string | null | undefined,
  requiredVersion: HeadsetVersion | string | null | undefined,
): boolean {
  return (
    isValidVersion(productVersion) &&
    isValidVersion(requiredVersion) &&
    compareVersions(productVersion, requiredVersion) >= 0
  );
}

/** Returns compareVersions' -1/0/1, or null when either side is not comparable. */
function compareVersionsSafely(
  a: HeadsetVersion | string | null | undefined,
  b: HeadsetVersion | string | null | undefined,
): number | null {
  if (!isValidVersion(a) || !isValidVersion(b)) return null;
  return compareVersions(a, b);
}

function isVersionEnough(productVersion: HeadsetVersion, requiredVersion: HeadsetVersion | null) {
  if (requiredVersion === null) return true;
  return isAtLeastVersion(productVersion, requiredVersion);
}

// --- Specific Version Checks ---

export function isVersionEnoughForSceneUpdateMessages(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, SCENE_UPDATE_MESSAGES);
}

export function isVersionEnoughForVoiceAssistant(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, VOICE_ASSISTANT);
}

export function isVersionEnoughForNewPatterns(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, NEW_PATTERNS);
}

export function isVersionEnoughForLanguages(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, NEW_LANGUAGES);
}

export function isVersionEnoughForLanguages2(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, NEW_LANGUAGES_2);
}

export function isVersionEnoughForVirtualAssistant(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, MIN_VIRTUAL_ASSISTANT_VERSION);
}

export function isVersionEnoughForLanguages3(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, NEW_LANGUAGES_3);
}

export function isVersionEnoughForFovea(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FOVEA);
}

export function isVersionEnoughForSwapAndKinetic(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.swap);
}

export function isVersionEnoughForFixationHandling(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FIXATION_HANDLING_UPDATE);
}

export function isVersionUSBUpdateNeeded(productVersion: HeadsetVersion) {
  if (!isValidVersion(productVersion)) return false;
  return compareVersions(productVersion, USB_UPDATE_NEEDED) < 0;
}

export function isPuiVersionEnough(puiVersion: string) {
  return isAtLeastVersion(puiVersion, "4.8.19");
}

export function isVersionEnoughForVolume(productVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.deviceVolumeControl);
}

export function isVersionEnoughForSceneReadyMessage(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.sceneReadyMessage);
}

export function isVersionEnoughForTumblingESceneReadyMessage(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.tumblingESceneReadyMessage);
}

export function isVersionEnoughForTumblingEUnityControlledRegularMode(
  productVersion: HeadsetVersion,
) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.tumblingEUnityControlledRegularMode);
}

export function isVersionEnoughForColorVisionAutomaticHeadsetDriven(
  productVersion: HeadsetVersion,
) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.colorVisionAutomaticHeadsetDriven);
}

export function isVersionEnoughForSkipVirtualAssistance(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.skipVirtualAssistance);
}

export function isVersionEnoughForStartWithCalibrationScene(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.startWithCalibrationScene);
}

export function isVersionEnoughForBackToBackEyeTest(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, HeadsetVersion.ver_2_8_5);
}

export function isVersionEnoughForDeviceIdleTimeouts(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.deviceIdleTimeouts);
}

export function isVersionEnoughForTestSession(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.testSession);
}

export function isVersionEnoughForOperatorPauseTest(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.operatorPauseTest);
}

export function isVersionEnoughForHeadsetSceneView(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.headsetSceneView);
}

export function isVersionEnoughForDeviceReadiness(productVersion: HeadsetVersion) {
  return isAtLeastVersion(productVersion, FEATURE_VERSION_MAP.deviceReadinessCheck);
}

export function isUpdateNeeded(version: HeadsetVersion, _osVersion: string) {
  return !isVersionEnoughForVoiceAssistant(version);
}

const LOWEST_HEADSET_VERSION = HeadsetVersion.ver_1_0;

function maxVersion(
  current: HeadsetVersion,
  candidate: HeadsetVersion | null | undefined,
): HeadsetVersion {
  if (!candidate) return current;
  return compareVersions(candidate, current) > 0 ? candidate : current;
}

function getChoiceValidSinceVersion(
  option: AvailableOption<unknown> | null | undefined,
  value: unknown,
): HeadsetVersion | null {
  if (!option || !Array.isArray(option.choices)) return null;
  const choice = option.choices.find((c) => c.value === value);
  return choice?.validSinceVersion ?? null;
}

export function getRequiredVersionForConfiguration(config: TestConfiguration): HeadsetVersion {
  let required: HeadsetVersion = LOWEST_HEADSET_VERSION;
  if (!config) return required;

  required = maxVersion(required, CATEGORY_VERSION_MAP[config.name]);

  const options = TEST_DEFINITIONS[config.name]?.availableOptions;
  if (!options) return required;

  required = maxVersion(required, options.testTypes?.validSinceVersion);
  required = maxVersion(required, getChoiceValidSinceVersion(options.testTypes, config.testType));

  if (config.testLanguage != null && options.languageOptions) {
    required = maxVersion(required, options.languageOptions.validSinceVersion);
    required = maxVersion(
      required,
      getChoiceValidSinceVersion(options.languageOptions, config.testLanguage),
    );
  }

  if (config.virtualAssistanceConfig?.showAssistant && options.virtualAssistantOptions) {
    required = maxVersion(required, options.virtualAssistantOptions.validSinceVersion);
    required = maxVersion(
      required,
      getChoiceValidSinceVersion(
        options.virtualAssistantOptions,
        config.virtualAssistanceConfig.assistantLanguage,
      ),
    );
  }

  if (config.voiceAssistantConfig?.isVoiceAssistantEnabled && options.voiceAssistantOptions) {
    required = maxVersion(required, options.voiceAssistantOptions.validSinceVersion);
    required = maxVersion(
      required,
      getChoiceValidSinceVersion(
        options.voiceAssistantOptions,
        config.voiceAssistantConfig.assistantLanguage,
      ),
    );
  }

  // Eye configuration.
  const eyeOptions = options.eyeConfigurationOptions;
  const eyeConfig = config.eyeConfiguration;
  if (eyeOptions && eyeConfig) {
    required = maxVersion(required, eyeOptions.eyeTypes?.validSinceVersion);
    required = maxVersion(
      required,
      getChoiceValidSinceVersion(eyeOptions.eyeTypes, eyeConfig.eyeType),
    );
    if (eyeConfig.isCalibrationNeeded) {
      required = maxVersion(required, eyeOptions.toggleCalibrationOption?.validSinceVersion);
    }
    if (eyeConfig.autoPauseForGaze) {
      required = maxVersion(required, eyeOptions.pauseOnFocusLostOption?.validSinceVersion);
    }
    if (eyeConfig.isTwoScreenFixationEnabled) {
      required = maxVersion(required, eyeOptions.toggleTwoScreenFixationOption?.validSinceVersion);
    }
  }

  // Test-specific options (extraData).
  const specificOptions = options.testSpecificConfigurationOptions;
  if (specificOptions && config.extraData) {
    const extraData = config.extraData;
    for (const key in specificOptions) {
      if (!Object.prototype.hasOwnProperty.call(specificOptions, key)) continue;
      const opt = (specificOptions as Record<string, AvailableOption<unknown> | undefined>)[key];
      if (!opt) continue;
      const fieldName = (opt.configurationFieldName ?? key) as keyof TestSpecificConfig;
      const value = extraData[fieldName];
      if (value === undefined) continue;

      const hasChoices = Array.isArray(opt.choices) && opt.choices.length > 0;
      if (hasChoices) {
        required = maxVersion(required, opt.validSinceVersion);
        required = maxVersion(required, getChoiceValidSinceVersion(opt, value));
      } else if (value === true) {
        required = maxVersion(required, opt.validSinceVersion);
      }
    }
  }

  return required;
}

export function getRequiredVersionForSuite(
  tests: { configuration: TestConfiguration }[] | null | undefined,
): HeadsetVersion {
  let required: HeadsetVersion = LOWEST_HEADSET_VERSION;
  if (!tests || tests.length === 0) return required;
  for (const test of tests) {
    if (!test?.configuration) continue;
    required = maxVersion(required, getRequiredVersionForConfiguration(test.configuration));
  }
  return required;
}

const RELEASE_CACHE_TTL_MS = 30_000;

/**
 * Coalesces concurrent calls and caches the resolved value for a short TTL.
 * getVersionStatus runs once per device card, and several cards can show the
 * same headset at once, so they would otherwise fire identical lookups.
 * The factory should reject on failure so a transient error is evicted and the
 * next call retries, rather than serving a poisoned entry for the whole TTL.
 */
function makeTtlPromiseCache<T>(ttlMs: number) {
  const cache = new Map<string, { promise: Promise<T>; expiresAt: number }>();
  return (key: string, factory: () => Promise<T>): Promise<T> => {
    const now = Date.now();
    for (const [k, entry] of cache) {
      if (entry.expiresAt <= now) cache.delete(k);
    }
    const cached = cache.get(key);
    if (cached && cached.expiresAt > now) return cached.promise;

    const promise = factory();
    cache.set(key, { promise, expiresAt: now + ttlMs });
    promise.catch(() => {
      if (cache.get(key)?.promise === promise) cache.delete(key);
    });
    return promise;
  };
}

const offersCache = makeTtlPromiseCache<ReleaseOffer[]>(RELEASE_CACHE_TTL_MS);

// A USB headset may belong to another org; the signed-in org's offers for its model then apply.
async function getOffersForUsbHeadset(pid: string, orgId: string | undefined) {
  try {
    return await getOffersForDevice(pid);
  } catch (error) {
    const isForbidden = axios.isAxiosError(error) && error.response?.status === 403;
    if (!isForbidden || !orgId) throw error;
    const hardwareModel = getHardwareModelFromPid(pid) ?? undefined;
    return offersCache(`org:${orgId}:${hardwareModel ?? ""}`, () =>
      getOffersForOrg(orgId, hardwareModel),
    );
  }
}

function getCachedOffers(pid: string | undefined): Promise<ReleaseOffer[]> {
  const orgId = store.getState().user.orgId;
  if (pid) return offersCache(`pid:${pid}`, () => getOffersForUsbHeadset(pid, orgId));
  return orgId ? offersCache(`org:${orgId}`, () => getOffersForOrg(orgId)) : Promise.resolve([]);
}

export interface VersionStatus {
  osUpdateReq: boolean;
  appUpdateReq: boolean;
  versionData: { latestApkVersion?: string };
  availableVersions: string[];
  lookupFailed: boolean;
}

export async function getVersionStatus(version: any, pid?: string): Promise<VersionStatus> {
  const noUpdate: VersionStatus = {
    osUpdateReq: false,
    appUpdateReq: false,
    versionData: {},
    availableVersions: [],
    lookupFailed: false,
  };
  if (!version) return noUpdate;
  let offers: ReleaseOffer[];
  try {
    offers = await getCachedOffers(pid);
  } catch {
    return { ...noUpdate, lookupFailed: true };
  }
  const availableVersions = offers
    .filter((offer) => isOfferedStatus(offer.status))
    .map((offer) => offer.version);
  const latestApkVersion = availableVersions[0];
  return {
    osUpdateReq: false,
    // Dev builds (e.g. "2.8.5_dev") do not compare and run ahead of the release.
    appUpdateReq: !!latestApkVersion && compareVersionsSafely(latestApkVersion, version) === 1,
    versionData: latestApkVersion ? { latestApkVersion } : {},
    availableVersions,
    lookupFailed: false,
  };
}

/**
 * @summary Scans through an AvailableOption and filters out choices that are not supported by the given productVersion.
 * If the top-level option itself is not supported, returns null.
 * @param option Object containing configuration option details and choices
 * @param productVersion Product version to check against
 * @param scopeList (Optional) List of available TestCategories for a given user. This depends on the user's license.
 * @returns
 */
export function filterAvailableOption<T>(
  option: AvailableOption<T>,
  productVersion: HeadsetVersion,
  scopeList?: T[],
): AvailableOption<T> | null {
  if (!option) return null;

  if (option.validSinceVersion) {
    if (!isVersionEnough(productVersion, option.validSinceVersion)) {
      console.log(`${option.validSinceVersion} is not supported for version ${productVersion}`);
      return null;
    }
  }

  if (option.choices && Array.isArray(option.choices)) {
    let filteredChoices = option.choices;
    filteredChoices = filteredChoices.filter((choice) => {
      try {
        if (!choice.validSinceVersion) {
          return true;
        }
        const isSupported = isVersionEnough(productVersion, choice.validSinceVersion);
        if (!isSupported) {
          console.log(`Test type ${choice} is not supported for version ${productVersion}`);
        }
        return isSupported;
      } catch (error) {
        console.error("Error while checking version support for :", choice, error);
        return false;
      }
    });

    if (scopeList && scopeList.length > 0) {
      filteredChoices = filteredChoices.filter((choice) => scopeList.includes(choice.value));
    }

    return { ...option, choices: filteredChoices };
  }

  return option;
}

function withoutTestTypeChoice(
  testTypes: AvailablePreTestConfigurationOptions["testTypes"],
  testType: TestType,
): AvailablePreTestConfigurationOptions["testTypes"] {
  return {
    ...testTypes,
    choices: (testTypes?.choices ?? []).filter((choice) => choice.value !== testType),
  };
}

export function filterTestConfigurationOptions(
  options: AvailablePreTestConfigurationOptions | null,
  productVersion: HeadsetVersion,
  scopeList?: string[],
): AvailablePreTestConfigurationOptions {
  // Deep copy to avoid mutating the original constant
  if (!options) {
    console.warn("No available options provided for filtering.");
    return {} as AvailablePreTestConfigurationOptions;
  }
  const newOptions = JSON.parse(JSON.stringify(options)) as AvailablePreTestConfigurationOptions;

  const filteredTestTypes = filterAvailableOption(newOptions.testTypes, productVersion);
  if (filteredTestTypes) {
    newOptions.testTypes = filteredTestTypes;
  } else {
    newOptions.testTypes = { ...options.testTypes, choices: [] };
  }

  // Scopes grant whole categories; anti-saccade needs its own entitlement inside Ocular Motility.
  if (scopeList && !scopeList.includes(SCOPE_TYPE.antiSaccade)) {
    newOptions.testTypes = withoutTestTypeChoice(newOptions.testTypes, TestType.AntiSaccades);
  }

  if (!hasVfaPattern24_2C(scopeList)) {
    newOptions.testTypes = withoutTestTypeChoice(newOptions.testTypes, TestType.PER_24_2C);
  }

  newOptions.languageOptions = filterAvailableOption(newOptions.languageOptions, productVersion);

  newOptions.virtualAssistantOptions = filterAvailableOption(
    newOptions.virtualAssistantOptions,
    productVersion,
  );

  // Only VFA defines voice assistant options; other tests must not gain the key
  // (with it, a saved suite's queue stayed empty in the mock-backend e2e flow).
  if (newOptions.voiceAssistantOptions) {
    newOptions.voiceAssistantOptions = supportsAssistantAudio(productVersion)
      ? null
      : filterAvailableOption(newOptions.voiceAssistantOptions, productVersion);
  }

  if (newOptions.eyeConfigurationOptions) {
    newOptions.eyeConfigurationOptions.eyeTypes = filterAvailableOption(
      newOptions.eyeConfigurationOptions.eyeTypes,
      productVersion,
    );

    newOptions.eyeConfigurationOptions.toggleCalibrationOption =
      scopeList && !scopeList.includes(SCOPE_TYPE.middleCalibration)
        ? null
        : filterAvailableOption(
            newOptions.eyeConfigurationOptions.toggleCalibrationOption,
            productVersion,
          );

    newOptions.eyeConfigurationOptions.pauseOnFocusLostOption = filterAvailableOption(
      newOptions.eyeConfigurationOptions.pauseOnFocusLostOption,
      productVersion,
    );
  }

  if (newOptions.testSpecificConfigurationOptions) {
    const specificOptions = newOptions.testSpecificConfigurationOptions;
    for (const key in specificOptions) {
      if (Object.prototype.hasOwnProperty.call(specificOptions, key)) {
        const filtered = filterAvailableOption(specificOptions[key], productVersion);

        if (filtered) {
          specificOptions[key] = filtered;
        } else {
          specificOptions[key] = null;
        }
      }
    }
  }

  if (scopeList && !hasVfaAdaptiveTracking(scopeList)) {
    const vfaSpecificOptions = newOptions.testSpecificConfigurationOptions as
      | AvailableVFASpecificConfigurationOptions
      | undefined;
    const behaviourOptions = vfaSpecificOptions?.vfaBehaviourOptions;

    if (behaviourOptions?.choices) {
      vfaSpecificOptions.vfaBehaviourOptions = {
        ...behaviourOptions,
        choices: behaviourOptions.choices.filter(
          (choice) => choice.value !== VFABehaviour.ADAPTIVE_TRACKING,
        ),
      };
    }
  }

  return newOptions;
}

function checkAndReplace(
  currentValue: AdjustableConfigurationValue,
  option: AvailableOption<typeof currentValue>,
) {
  // First check if option is boolean
  if (typeof currentValue === "boolean") {
    return currentValue;
  }

  if (
    !option ||
    !option.choices ||
    (Array.isArray(option.choices) && option.choices.length === 0)
  ) {
    // Below case should not happen. For safety, we log a warning and return current value.
    console.warn(
      `${option?.configurationFieldName} has no choices defined. This should not happen for non-boolean fields. Returning current value: ${currentValue} as is. Please check the available options configuration.`,
    );
    return currentValue;
  }
  const choices = getChoiceValueListFromOption(option);
  if (!choices.includes(currentValue)) {
    console.warn(
      `Value ${currentValue} for ${option.configurationFieldName} is not available. Replacing with ${choices[0]}`,
    );
    return choices[0];
  }
  return currentValue;
}

/**
 * @summary Given a list of available options, ensures that the test configuration contains the values that exist within the available options.
 * This is useful in scenarios where the available options might have changed due to version updates,
 * and we want to ensure that the test configuration remains valid.
 * @param configuration TestConfiguration object to sanitize
 * @param availableOptions AvailablePreTestConfigurationOptions object containing the available options
 * @returns TestConfiguration Sanitized TestConfiguration object
 */
/**
 * Tests that must always run midpoint calibration, even when the calibration
 * toggle is unavailable (e.g. the user lacks the middleCalibration scope).
 */
const CALIBRATION_REQUIRED_WITHOUT_SCOPE = new Set<TestCategory>([
  TestCategory.VisualField,
  TestCategory.OcularMotility,
  TestCategory.FDT,
]);

const TOGGLE_FIELDS_DEFAULTING_OFF = new Set<string>([
  "fovealThresholdTestingEnabled",
  "childMode",
  "showTutorial",
]);

export function sanitizeTestConfiguration(
  configuration: TestConfiguration,
  availableOptions: AvailablePreTestConfigurationOptions,
): TestConfiguration {
  const testName = configuration?.name;
  const testType = configuration?.testType;
  const isOriginalBinocularTest =
    testName === TestCategory.VisualField && isBinocularVFATestType(testType);
  const sanitizedConfig = { ...configuration };

  //Sanitize Test Type
  if (availableOptions.testTypes && availableOptions.testTypes.choices?.length > 0) {
    sanitizedConfig.testType = checkAndReplace(
      sanitizedConfig.testType,
      availableOptions.testTypes,
    ) as TestType;
  } else if (isOriginalBinocularTest) {
    delete sanitizedConfig.testType;
  }

  const shouldAccountForBinocularTests =
    testName === TestCategory.VisualField && isBinocularVFATestType(sanitizedConfig.testType);

  // 2. Sanitize Language
  if (availableOptions.languageOptions && availableOptions.languageOptions.choices?.length > 0) {
    sanitizedConfig.testLanguage = checkAndReplace(
      sanitizedConfig.testLanguage,
      availableOptions.languageOptions,
    ) as TestLanguage;
  } else {
    sanitizedConfig.testLanguage = null;
  }

  // 2.5 Sanitize Test Speed
  if (availableOptions.testSpeedOptions && availableOptions.testSpeedOptions.choices?.length > 0) {
    sanitizedConfig.testSpeed = checkAndReplace(
      sanitizedConfig.testSpeed,
      availableOptions.testSpeedOptions,
    ) as WaitDuration;
  } else {
    sanitizedConfig.testSpeed = null;
  }

  // 3. Sanitize Eye Configuration
  if (availableOptions.eyeConfigurationOptions) {
    const eyeConfigOptions = availableOptions.eyeConfigurationOptions;
    let newEyeConfig = { ...sanitizedConfig.eyeConfiguration };

    if (shouldAccountForBinocularTests) {
      newEyeConfig = { ...newEyeConfig, eyeType: EyeType.BothEye };
    } else if (eyeConfigOptions?.eyeTypes) {
      newEyeConfig = {
        ...newEyeConfig,
        eyeType: checkAndReplace(newEyeConfig.eyeType, eyeConfigOptions.eyeTypes) as EyeType,
      };
    } else {
      delete newEyeConfig.eyeType;
    }
    if (eyeConfigOptions.toggleCalibrationOption) {
      newEyeConfig = {
        ...newEyeConfig,
        isCalibrationNeeded:
          typeof newEyeConfig.isCalibrationNeeded === "boolean"
            ? newEyeConfig.isCalibrationNeeded
            : true,
      };
    } else if (CALIBRATION_REQUIRED_WITHOUT_SCOPE.has(testName)) {
      // The calibration toggle is hidden (e.g. user lacks the middleCalibration
      // scope), but these tests must always run calibration.
      newEyeConfig = { ...newEyeConfig, isCalibrationNeeded: true };
    } else {
      delete newEyeConfig.isCalibrationNeeded;
    }
    if (eyeConfigOptions.pauseOnFocusLostOption) {
      newEyeConfig = {
        ...newEyeConfig,
        autoPauseForGaze: checkAndReplace(
          newEyeConfig.autoPauseForGaze,
          eyeConfigOptions.pauseOnFocusLostOption,
        ) as boolean,
      };
    } else {
      delete newEyeConfig.autoPauseForGaze;
    }
    if (eyeConfigOptions.toggleTwoScreenFixationOption) {
      newEyeConfig = {
        ...newEyeConfig,
        isTwoScreenFixationEnabled: checkAndReplace(
          newEyeConfig.isTwoScreenFixationEnabled,
          eyeConfigOptions.toggleTwoScreenFixationOption,
        ) as boolean,
      };
    } else {
      delete newEyeConfig.isTwoScreenFixationEnabled;
    }
    sanitizedConfig.eyeConfiguration = newEyeConfig;
  }

  // 4. Sanitize Virtual Assistant Config
  if (availableOptions.virtualAssistantOptions && sanitizedConfig.virtualAssistanceConfig) {
    sanitizedConfig.virtualAssistanceConfig = {
      ...sanitizedConfig.virtualAssistanceConfig,
      assistantLanguage: checkAndReplace(
        sanitizedConfig.virtualAssistanceConfig.assistantLanguage,
        availableOptions.virtualAssistantOptions,
      ) as TestLanguage,
    };
  } else {
    sanitizedConfig.virtualAssistanceConfig = null;
  }

  if (availableOptions.voiceAssistantOptions && sanitizedConfig.voiceAssistantConfig) {
    sanitizedConfig.voiceAssistantConfig = {
      ...sanitizedConfig.voiceAssistantConfig,
      assistantLanguage: checkAndReplace(
        sanitizedConfig.voiceAssistantConfig.assistantLanguage,
        availableOptions.voiceAssistantOptions,
      ) as TestLanguage,
    };
  } else {
    sanitizedConfig.voiceAssistantConfig = null;
  }

  // Sanitize Test Specific Options
  if (sanitizedConfig.extraData) {
    const newExtraData: TestSpecificConfig = { ...sanitizedConfig.extraData };
    // Record views: the unions have no string index signature, so iterating by
    // name needs a generic record to avoid `never` from keyof-union.
    const extraDataRecord = newExtraData as Record<string, AdjustableConfigurationValue>;
    const specificOptions = availableOptions.testSpecificConfigurationOptions as
      | Record<string, AvailableOption<AdjustableConfigurationValue> | null | undefined>
      | undefined;

    const allowedFieldNames = new Set<string>();
    if (specificOptions) {
      for (const optKey in specificOptions) {
        if (!Object.prototype.hasOwnProperty.call(specificOptions, optKey)) continue;
        const opt = specificOptions[optKey];
        if (!opt) continue;
        const fieldName = opt.configurationFieldName ?? optKey;
        allowedFieldNames.add(fieldName);
      }
    }

    if (specificOptions) {
      for (const optKey in specificOptions) {
        if (!Object.prototype.hasOwnProperty.call(specificOptions, optKey)) continue;
        const testSpecificConfigOption = specificOptions[optKey];
        if (!testSpecificConfigOption) continue;
        const fieldName = testSpecificConfigOption.configurationFieldName ?? optKey;
        if (isFreeFormNumberOption(testSpecificConfigOption)) {
          continue;
        }
        // treat options without choices (toggle) as boolean fields and ensure presence
        if (
          (!testSpecificConfigOption.choices || testSpecificConfigOption.choices.length === 0) &&
          !Object.prototype.hasOwnProperty.call(newExtraData, fieldName)
        ) {
          extraDataRecord[fieldName] = !TOGGLE_FIELDS_DEFAULTING_OFF.has(fieldName);
        } else if (
          testSpecificConfigOption.choices &&
          testSpecificConfigOption.choices.length > 0 &&
          !Object.prototype.hasOwnProperty.call(newExtraData, fieldName)
        ) {
          // if the field is missing, set it to the first available choice
          const choices = getChoiceValueListFromOption(testSpecificConfigOption);
          extraDataRecord[fieldName] = choices[0];
        }
      }
    }

    for (const optKey in newExtraData) {
      if (!Object.prototype.hasOwnProperty.call(newExtraData, optKey)) continue;

      if (!allowedFieldNames.has(optKey)) {
        delete extraDataRecord[optKey];
        continue;
      }

      const opt = specificOptions
        ? Object.values(specificOptions).find((o) => (o?.configurationFieldName ?? "") === optKey)
        : undefined;

      if (isFreeFormNumberOption(opt)) {
        const freeFormValue = extraDataRecord[optKey];
        if (typeof freeFormValue !== "number" || !Number.isFinite(freeFormValue)) {
          delete extraDataRecord[optKey];
        }
      } else if (opt) {
        extraDataRecord[optKey] = checkAndReplace(
          extraDataRecord[optKey],
          opt,
        ) as AdjustableConfigurationValue;
      } else {
        // Config has not been found. This can happen if the configuration holds a field that is not determined by available options.
        console.warn(
          `Test specific configuration field ${optKey} not found in available options. Leaving value as is.`,
        );
      }
    }

    sanitizedConfig.extraData = newExtraData;
  } else {
    delete sanitizedConfig.extraData;
  }

  return sanitizedConfig;
}
